import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(t('fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      // In sandbox mode, we get the token back for testing
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast.success('Lyen reset voye!');
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Erè. Tanpri eseye ankò.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 shadow-lg text-center">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-mrc-navy mb-4">
              Tcheke Email Ou
            </h1>
            <p className="text-muted-foreground mb-6">
              Si email <strong>{email}</strong> egziste nan sistèm nou an, ou pral resevwa yon lyen pou reset modpas ou.
            </p>

            {/* Show reset link for testing (sandbox mode) */}
            {resetToken && (
              <div className="bg-mrc-orange/10 border border-mrc-orange/30 p-4 rounded mb-6">
                <p className="text-sm text-mrc-navy font-medium mb-2">🧪 Mode Test - Lyen Reset:</p>
                <Link 
                  to={`/reset-password?token=${resetToken}`}
                  className="text-mrc-orange hover:underline break-all text-sm"
                >
                  Klike la pou reset modpas ou
                </Link>
              </div>
            )}

            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retounen nan Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 shadow-lg">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_mwenkomerce/artifacts/xt1gnrb8_IMG_1789.png" 
              alt="MRC Boutique Logo" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="font-heading text-2xl font-bold text-mrc-navy">
              Bliye Modpas?
            </h1>
            <p className="text-muted-foreground mt-2">
              Antre email ou pou resevwa yon lyen pou reset modpas ou.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-10"
                  data-testid="email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-mrc-navy hover:bg-mrc-navy/90 text-white py-6 text-lg font-semibold uppercase tracking-wider rounded-full"
              data-testid="submit-btn"
            >
              {loading ? 'Ap voye...' : 'Voye Lyen Reset'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-mrc-navy font-semibold hover:underline inline-flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Retounen nan Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
